import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { DestinationPicker } from "@/components/DestinationPicker";
import { EditableCell } from "@/components/EditableCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import {
  Menu, Plus, Trash2, Search, Save, FileDown, ArrowLeft,
  Building2, ShoppingBag, Calculator, Lock, Star,
} from "lucide-react";

export default function QuoteEditor() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const isEditing = !!quoteId;
  const basePath = user?.role === "agent" ? "/agent" : "/admin";
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  // Client
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [selectedDest, setSelectedDest] = useState(null);
  const [clientPrices, setClientPrices] = useState({}); // product_id -> {price, source, sc1, sc2}
  const [clientScontiFissi, setClientScontiFissi] = useState(false);
  const [scontoCanale, setScontoCanale] = useState(0);

  // Products
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showProductPicker, setShowProductPicker] = useState(false);

  // Quote items
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const [clientsRes, productsRes] = await Promise.all([
          authAxios.get("/clients"),
          authAxios.get("/products"),
        ]);
        setClients(clientsRes.data);
        setProducts(productsRes.data);

        if (isEditing) {
          const quoteRes = await authAxios.get(`/quotes/${quoteId}`);
          const q = quoteRes.data;
          setSelectedClient({
            id: q.client_id,
            ragione_sociale: q.client_name,
            partita_iva: q.client_partita_iva,
            indirizzo: q.client_indirizzo,
            citta: q.client_citta,
          });
          setItems(q.items.map((it, i) => ({ ...it, _key: Date.now() + i })));
          setNote(q.note || "");
        }
      } catch {
        toast.error("Errore caricamento dati");
      } finally { setLoading(false); }
    };
    init();
  }, [authAxios, isEditing, quoteId]);

  // Client picker
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 50);
    const q = clientSearch.toLowerCase();
    return clients.filter(c =>
      (c.ragione_sociale || "").toLowerCase().includes(q) ||
      (c.partita_iva || "").includes(q) ||
      (c.citta || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [clients, clientSearch]);

  const selectClient = async (c) => {
    setSelectedClient(c);
    setSelectedDest(null);
    setShowClientPicker(false);
    setClientSearch("");
    setClientScontiFissi(!!c.sconti_fissi);
    try {
      const allProductIds = products.map((p) => p.id);
      const [destRes, resolvedRes] = await Promise.all([
        authAxios.get(`/clients/${c.id}/destinations`).catch(() => ({ data: [] })),
        allProductIds.length > 0
          ? authAxios.post(`/clients/${c.id}/resolve-prices`, { product_ids: allProductIds }).catch(() => ({ data: { prices: {} } }))
          : Promise.resolve({ data: { prices: {} } }),
      ]);
      setDestinations(destRes.data);
      setClientPrices(resolvedRes.data.prices || {});
      setScontoCanale(resolvedRes.data.sconto_canale || 0);
    } catch {
      console.error("Failed to load destinations/prices");
      setDestinations([]);
      setClientPrices({});
    }
  };

  // Product picker
  const filteredProducts = useMemo(() => {
    if (!productSearch) return [];
    const q = productSearch.toLowerCase();
    return products.filter(p =>
      !p.non_disponibile && (
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.category_name || "").toLowerCase().includes(q)
      )
    ).slice(0, 30);
  }, [products, productSearch]);

  const addProduct = (p) => {
    const exists = items.find(it => it.product_id === p.id);
    if (exists) {
      toast.info("Prodotto gia aggiunto, modifica la quantita");
      return;
    }
    const resolved = clientPrices[p.id];
    const isManual = resolved?.source === "manual";
    setItems(prev => [...prev, {
      _key: Date.now(),
      product_id: p.id,
      product_name: p.name,
      product_sku: p.sku,
      quantity: 1,
      qty_omaggio: 0,
      unit: p.unit || "pz",
      price_unit: isManual ? resolved.price : (p.price_list || 0),
      sc1: isManual ? 0 : (resolved?.sc1 || 0),
      sc2: isManual ? 0 : (resolved?.sc2 || 0),
      sc3: isManual ? 0 : (resolved?.sc3 || 0),
      sc4: isManual ? 0 : (resolved?.sc4 || 0),
      iva_percentage: p.iva_percentage ?? 10,
    }]);
    setProductSearch("");
    setShowProductPicker(false);
  };

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(it =>
      it._key === key ? { ...it, [field]: value } : it
    ));
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(it => it._key !== key));
  };

  // Totals
  const totals = useMemo(() => {
    let merce = 0;
    const ivaMap = {};
    items.forEach(it => {
      const qty = it.quantity || 0;
      const price = it.price_unit || 0;
      const s1 = parseFloat(it.sc1) || 0;
      const s2 = parseFloat(it.sc2) || 0;
      const s3 = parseFloat(it.sc3) || 0;
      const s4 = parseFloat(it.sc4) || 0;
      let lineTotal = qty * price;
      if (s1 > 0) lineTotal *= (1 - s1 / 100);
      if (s2 > 0) lineTotal *= (1 - s2 / 100);
      if (s3 > 0) lineTotal *= (1 - s3 / 100);
      if (s4 > 0) lineTotal *= (1 - s4 / 100);
      if (scontoCanale > 0) lineTotal *= (1 - scontoCanale / 100);
      lineTotal = Math.round(lineTotal * 100) / 100;
      merce += lineTotal;
      const iva = it.iva_percentage || 0;
      ivaMap[iva] = (ivaMap[iva] || 0) + lineTotal * iva / 100;
    });
    const ivaDetails = Object.entries(ivaMap)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([aliquota, importo]) => ({ aliquota: Number(aliquota), importo: Math.round(importo * 100) / 100 }));
    const ivaTotal = ivaDetails.reduce((s, d) => s + d.importo, 0);
    return {
      merce: Math.round(merce * 100) / 100,
      ivaDetails,
      ivaTotal: Math.round(ivaTotal * 100) / 100,
      total: Math.round((merce + ivaTotal) * 100) / 100,
    };
  }, [items, scontoCanale]);

  const handleSave = async () => {
    if (!selectedClient) { toast.error("Seleziona un cliente"); return; }
    if (items.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient.id,
        client_name: selectedClient.ragione_sociale,
        client_partita_iva: selectedClient.partita_iva,
        client_indirizzo: selectedDest ? selectedDest.indirizzo : selectedClient.indirizzo,
        client_citta: selectedDest ? selectedDest.citta : selectedClient.citta,
        destination_id: selectedDest?.id || null,
        destination_label: selectedDest ? `${selectedDest.denominazione || selectedDest.indirizzo}` : null,
        items: items.map(({ _key, row_total, row_iva, ...rest }) => rest),
        sconto_canale: scontoCanale || 0,
        note: note || null,
      };
      if (isEditing) {
        await authAxios.put(`/quotes/${quoteId}`, payload);
        toast.success("Preventivo aggiornato");
      } else {
        await authAxios.post("/quotes", payload);
        toast.success("Preventivo creato");
      }
      // Auto-save client prices DISABILITATO (richiesta utente 2026-04-22)
      // Lavoriamo solo con i client_prices ufficiali importati. Per riattivare, rimuovere il commento.
      // if (selectedClient?.id && items.length > 0) {
      //   try {
      //     await authAxios.post(`/clients/${selectedClient.id}/prices/bulk`, {
      //       items: items.map((it) => {
      //         let eff = parseFloat(it.price_unit) || 0;
      //         for (const s of [it.sc1, it.sc2, it.sc3, it.sc4]) {
      //           const v = parseFloat(s) || 0;
      //           if (v > 0) eff *= (1 - v / 100);
      //         }
      //         return { product_id: it.product_id, custom_price: Math.round(eff * 100) / 100 };
      //       }),
      //     });
      //   } catch (e) {
      //     console.error("Failed to bulk-save client prices:", e);
      //   }
      // }
      navigate(`${basePath}/quotes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const handleSaveAndPdf = async () => {
    if (!selectedClient) { toast.error("Seleziona un cliente"); return; }
    if (items.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient.id,
        client_name: selectedClient.ragione_sociale,
        client_partita_iva: selectedClient.partita_iva,
        client_indirizzo: selectedDest ? selectedDest.indirizzo : selectedClient.indirizzo,
        client_citta: selectedDest ? selectedDest.citta : selectedClient.citta,
        destination_id: selectedDest?.id || null,
        destination_label: selectedDest ? `${selectedDest.denominazione || selectedDest.indirizzo}` : null,
        items: items.map(({ _key, row_total, row_iva, ...rest }) => rest),
        sconto_canale: scontoCanale || 0,
        note: note || null,
      };
      let qId;
      if (isEditing) {
        await authAxios.put(`/quotes/${quoteId}`, payload);
        qId = quoteId;
      } else {
        const res = await authAxios.post("/quotes", payload);
        qId = res.data.id;
      }
      // Download PDF
      const pdfRes = await authAxios.post(`/quotes/${qId}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
      const a = document.createElement("a"); a.href = url; a.download = "preventivo.pdf"; a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Preventivo salvato e PDF scaricato");
      navigate(`${basePath}/quotes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="quote-editor-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-5xl">
        <PageHeader title="Preventivo" />
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/quotes`)}
            className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-white flex-1">
            {isEditing ? "Modifica Preventivo" : "Nuovo Preventivo"}
          </h1>
        </div>

        {/* Client Section */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-400" /> Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedClient ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{selectedClient.ragione_sociale}</p>
                  <p className="text-zinc-500 text-xs">
                    {selectedClient.partita_iva && `P.IVA: ${selectedClient.partita_iva}`}
                    {selectedClient.citta && ` - ${selectedClient.citta}`}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {clientScontiFissi && (
                      <span className="text-red-400 text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Sconti fissi</span>
                    )}
                    {Object.values(clientPrices).filter(v => v.source !== "none").length > 0 && (
                      <span className="text-emerald-400 text-xs flex items-center gap-1"><Star className="w-3 h-3" /> {Object.values(clientPrices).filter(v => v.source !== "none").length} sconti</span>
                    )}
                    {scontoCanale > 0 && (
                      <span className="text-cyan-400 text-xs">Canale: {scontoCanale}%</span>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowClientPicker(true)}
                  className="border-white/10 text-zinc-400">Cambia</Button>
              </div>
            ) : (
              <Button onClick={() => setShowClientPicker(true)}
                variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 w-full"
                data-testid="select-client-btn">
                <Search className="w-4 h-4 mr-2" /> Seleziona Cliente
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Destination Picker */}
        {selectedClient && (
          <div className="mb-4">
            <DestinationPicker
              clientId={selectedClient.id}
              clientAddress={selectedClient.indirizzo || ""}
              selectedDest={selectedDest}
              onSelect={(dest) => setSelectedDest(dest)}
              destinations={destinations}
            />
          </div>
        )}

        {/* Products Section */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-amber-400" /> Prodotti ({items.length})
              </CardTitle>
              <Button size="sm" onClick={() => setShowProductPicker(true)}
                className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <ShoppingBag className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
                <p>Nessun prodotto aggiunto</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5 text-zinc-400 text-xs">
                      <th className="text-left p-2">Prodotto</th>
                      <th className="text-center p-2" style={{width:"50px"}}>Qtà</th>
                      <th className="text-right p-2" style={{width:"55px"}}>Prezzo</th>
                      <th className="text-right p-2" style={{width:"55px"}}>Tot.</th>
                      <th style={{width:"28px"}} className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const qty = it.quantity || 0;
                      const price = it.price_unit || 0;
                      let lineTotal = qty * price;
                      for (const s of [it.sc1, it.sc2, it.sc3, it.sc4]) {
                        const v = parseFloat(s) || 0;
                        if (v > 0) lineTotal *= (1 - v / 100);
                      }
                      if (scontoCanale > 0) lineTotal *= (1 - scontoCanale / 100);
                      lineTotal = Math.round(lineTotal * 100) / 100;
                      const isLocked = clientScontiFissi && user?.role === "agent";
                      return (
                        <React.Fragment key={it._key}>
                        <tr className="border-b-0">
                          <td className="pt-2 pb-0 px-3">
                            <p className="text-white text-xs font-medium">{it.product_name}</p>
                            <span className="text-zinc-500 text-[10px] font-mono">{it.product_sku}</span>
                          </td>
                          <td className="pt-2 pb-0 px-2">
                            <EditableCell value={it.quantity} decimals={3}
                              onChange={(v) => updateItem(it._key, "quantity", v)}
                              className="w-14 text-center bg-[#27272a] border-white/10 h-7 text-xs"
                              data-testid={`qty-${it._key}`} />
                          </td>
                          <td className="pt-2 pb-0 px-2">
                            <EditableCell value={it.price_unit} decimals={2}
                              onChange={(v) => updateItem(it._key, "price_unit", v)}
                              disabled={isLocked}
                              className="w-18 text-right bg-[#27272a] border-white/10 h-7 text-xs font-mono disabled:opacity-60"
                              data-testid={`price-${it._key}`} />
                          </td>
                          <td className="pt-2 pb-0 px-3 text-right font-mono text-white font-medium text-xs">
                            {lineTotal.toFixed(2)}
                          </td>
                          <td className="pt-2 pb-0 px-2">
                            <Button size="sm" variant="ghost" onClick={() => removeItem(it._key)}
                              className="text-zinc-500 hover:text-red-400 h-6 w-6 p-0">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td colSpan={5} className="pb-2 pt-1 px-3">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-emerald-500/70 text-[10px] w-6">Sc%</span>
                              <EditableCell value={it.sc1} decimals={1} placeholder="-"
                                onChange={(v) => updateItem(it._key, "sc1", v)} disabled={isLocked}
                                className="w-12 text-center bg-[#18181b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                              <span className="text-zinc-600 text-[10px]">+</span>
                              <EditableCell value={it.sc2} decimals={1} placeholder="-"
                                onChange={(v) => updateItem(it._key, "sc2", v)} disabled={isLocked}
                                className="w-12 text-center bg-[#18181b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                              <span className="text-zinc-600 text-[10px]">+</span>
                              <EditableCell value={it.sc3} decimals={1} placeholder="-"
                                onChange={(v) => updateItem(it._key, "sc3", v)} disabled={isLocked}
                                className="w-12 text-center bg-[#18181b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                              <span className="text-zinc-600 text-[10px]">+</span>
                              <EditableCell value={it.sc4} decimals={1} placeholder="-"
                                onChange={(v) => updateItem(it._key, "sc4", v)} disabled={isLocked}
                                className="w-12 text-center bg-[#18181b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                              {scontoCanale > 0 && (
                                <>
                                  <span className="text-zinc-600 text-[10px]">+</span>
                                  <span className="text-cyan-400 text-[10px] font-mono bg-cyan-500/10 px-1.5 py-0.5 rounded">C:{scontoCanale}</span>
                                </>
                              )}
                              <span className="text-zinc-600 text-[10px] ml-2">|</span>
                              <span className="text-fuchsia-500/70 text-[10px]">Om.</span>
                              <EditableCell value={it.qty_omaggio || 0} decimals={3} placeholder="0"
                                onChange={(v) => updateItem(it._key, "qty_omaggio", Math.max(0, v))}
                                className="w-12 text-center bg-[#18181b] border-fuchsia-500/30 text-fuchsia-400 h-6 text-[10px] px-1" />
                            </div>
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Note */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardContent className="p-4">
            <Label className="text-zinc-300 text-xs mb-2 block">Note</Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={3} placeholder="Note aggiuntive per il preventivo..."
              className="w-full bg-[#27272a] border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              data-testid="quote-note" />
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="bg-[#18181b] border-white/5 mb-6">
          <CardContent className="p-4">
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Merce{scontoCanale > 0 ? ` (incl. Sc.C ${scontoCanale}%)` : ""}</span>
                  <span className="text-white font-mono">{totals.merce.toFixed(2)}</span>
                </div>
                {totals.ivaDetails.map(d => (
                  <div key={d.aliquota} className="flex justify-between text-sm">
                    <span className="text-zinc-400">IVA {d.aliquota}%</span>
                    <span className="text-white font-mono">{d.importo.toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-amber-500/30 pt-2 flex justify-between">
                  <span className="text-white font-semibold">TOTALE</span>
                  <span className="text-amber-400 font-bold font-mono text-lg">{totals.total.toFixed(2)} EUR</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 justify-end">
          <Button variant="outline" onClick={() => navigate(`${basePath}/quotes`)}
            className="border-white/10 text-zinc-400">Annulla</Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-quote-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Preventivo"}
          </Button>
          <Button onClick={handleSaveAndPdf} disabled={saving}
            variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            data-testid="save-and-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" /> Salva e Scarica PDF
          </Button>
        </div>

        {/* Client Picker Dialog */}
        <Dialog open={showClientPicker} onOpenChange={setShowClientPicker}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-white">Seleziona Cliente</DialogTitle>
            </DialogHeader>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Cerca per nome, P.IVA, citta..."
                value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                className="pl-10 bg-[#27272a] border-white/10" data-testid="client-picker-search"
                autoFocus />
            </div>
            <div className="overflow-y-auto max-h-[50vh] space-y-1">
              {filteredClients.map(c => (
                <button key={c.id} onClick={() => selectClient(c)}
                  className="w-full text-left p-3 rounded-lg hover:bg-white/5 transition-colors">
                  <p className="text-white text-sm font-medium">{c.ragione_sociale}</p>
                  <p className="text-zinc-500 text-xs">
                    {c.partita_iva && `P.IVA: ${c.partita_iva}`}
                    {c.citta && ` - ${c.citta}`}
                    {c.cod_agente != null && ` | Ag. ${c.cod_agente}`}
                  </p>
                </button>
              ))}
              {filteredClients.length === 0 && (
                <p className="text-zinc-500 text-center py-4">Nessun cliente trovato</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Product Picker Dialog */}
        <Dialog open={showProductPicker} onOpenChange={setShowProductPicker}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-white">Aggiungi Prodotto</DialogTitle>
            </DialogHeader>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Cerca per nome, SKU, categoria..."
                value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10 bg-[#27272a] border-white/10" data-testid="product-picker-search"
                autoFocus />
            </div>
            <div className="overflow-y-auto max-h-[50vh] space-y-1">
              {productSearch && filteredProducts.map(p => (
                <button key={p.id} onClick={() => addProduct(p)}
                  className="w-full text-left p-3 rounded-lg hover:bg-white/5 transition-colors flex justify-between items-center">
                  <div>
                    <p className="text-white text-sm font-medium">{p.name}</p>
                    <p className="text-zinc-500 text-xs">
                      SKU: {p.sku} | {p.category_name}
                    </p>
                  </div>
                  <div className="text-right">
                    {clientPrices[p.id]?.sc1 > 0 && (
                      <p className="text-emerald-400 font-mono text-xs">
                        -{clientPrices[p.id].sc1}%{clientPrices[p.id]?.sc2 > 0 ? `/-${clientPrices[p.id].sc2}%` : ""}
                      </p>
                    )}
                    <p className="text-zinc-400 font-mono text-sm">{(p.price_list || 0).toFixed(2)}</p>
                    <p className="text-zinc-500 text-[10px]">IVA {p.iva_percentage ?? "N/A"}%</p>
                  </div>
                </button>
              ))}
              {productSearch && filteredProducts.length === 0 && (
                <p className="text-zinc-500 text-center py-4">Nessun prodotto trovato</p>
              )}
              {!productSearch && (
                <p className="text-zinc-500 text-center py-4">Digita per cercare un prodotto</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
